import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import { industryLabel } from './utils';
import AutocompleteInput from './AutocompleteInput';
import useAutocomplete from '../hooks/useAutocomplete';
import './RelationshipManagers.css';
import './FiltersDialog.css';

/** The Graph/Tree overview's filters, as a dialog (opened from TopBar's Filters button). Hidden
 * entirely on Pipeline/Dashboard - Graph.js never opens it for those views. */
const FiltersDialog = ({
  isOpen,
  onClose,
  loading,
  groupBy = 'industry',
  onGroupByChange = () => {},
  companyNameFilter,
  onCompanyNameFilterChange,
  contactNameFilter,
  onContactNameFilterChange,
  positionFilter,
  onPositionFilterChange,
  industries = [],
  selectedIndustry,
  onIndustryChange,
  advisors = [],
  selectedAdvisor,
  onAdvisorChange,
  onApplyFilters,
  hasPendingFilterChanges
}) => {
  const dialogRef = useRef(null);

  const companyAutocomplete = useAutocomplete('company', {
    debounceMs: 300,
    minLength: 2,
    pageSize: 10,
    onSelect: (company) => {
      onCompanyNameFilterChange(company.name);
    },
    disabled: loading
  });

  const positionAutocomplete = useAutocomplete('position', {
    debounceMs: 300,
    minLength: 2,
    pageSize: 10,
    onSelect: (position) => {
      onPositionFilterChange(position.name);
    },
    disabled: loading
  });

  useEffect(() => {
    if (companyNameFilter !== companyAutocomplete.query) {
      companyAutocomplete.setQuery(companyNameFilter || '');
    }
  }, [companyNameFilter, companyAutocomplete]);

  useEffect(() => {
    if (positionFilter !== positionAutocomplete.query) {
      positionAutocomplete.setQuery(positionFilter || '');
    }
  }, [positionFilter, positionAutocomplete]);

  useEffect(() => {
    if (isOpen) dialogRef.current?.focus();
  }, [isOpen]);

  // Escape closes the dialog; it doesn't reach the panels underneath
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="rm-overlay" onClick={onClose} aria-hidden="true" />
      <div className="rm-dialog filters-dialog" role="dialog" aria-modal="true" aria-labelledby="filters-dialog-title"
           ref={dialogRef} tabIndex={-1}>
        <header className="rm-dialog__header">
          <h2 id="filters-dialog-title"><Icon name="filter" /> Filters</h2>
          <button type="button" className="rm-icon-button" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>

        <div className="rm-dialog__body filters-dialog__body">
          <div className="filters-dialog__grid">
            <div className="filter-dropdown">
              <label>Group by</label>
              <div className="top-bar-view-toggle" role="group" aria-label="Group by">
                <button
                  type="button"
                  aria-pressed={groupBy === 'industry'}
                  onClick={() => onGroupByChange('industry')}
                  className={`top-bar-view-button ${groupBy === 'industry' ? 'active' : ''}`}
                  title="Group companies by industry"
                >
                  Industry
                </button>
                <button
                  type="button"
                  aria-pressed={groupBy === 'icp'}
                  onClick={() => onGroupByChange('icp')}
                  className={`top-bar-view-button ${groupBy === 'icp' ? 'active' : ''}`}
                  title="Show the ICP target-account lists"
                >
                  ICP
                </button>
              </div>
            </div>

            <div className="filter-dropdown">
              <label htmlFor="company-name-filter">Company Name</label>
              <AutocompleteInput
                value={companyNameFilter || ''}
                onChange={(value) => {
                  companyAutocomplete.setQuery(value);
                  onCompanyNameFilterChange(value);
                }}
                onSelect={companyAutocomplete.handleSelect}
                suggestions={companyAutocomplete.suggestions}
                loading={companyAutocomplete.loading}
                placeholder="Filter by company..."
                disabled={loading}
                showLogos={true}
                itemType="company"
                totalCount={companyAutocomplete.totalCount}
                hasMore={companyAutocomplete.hasMore}
                onLoadMore={companyAutocomplete.loadMore}
                currentPage={companyAutocomplete.currentPage}
                pageSize={10}
                onPageChange={companyAutocomplete.handlePageChange}
                showPagination={false} // Use infinite scroll instead
              />
            </div>

            <div className="filter-dropdown">
              <label htmlFor="contact-name-filter">Contact Name</label>
              <input
                id="contact-name-filter"
                type="text"
                value={contactNameFilter || ''}
                onChange={(e) => onContactNameFilterChange(e.target.value)}
                placeholder="Filter by contact..."
                className="filter-input"
                disabled={loading}
              />
            </div>

            <div className="filter-dropdown">
              <label htmlFor="position-filter">Position</label>
              <AutocompleteInput
                value={positionFilter || ''}
                onChange={(value) => {
                  positionAutocomplete.setQuery(value);
                  onPositionFilterChange(value);
                }}
                onSelect={positionAutocomplete.handleSelect}
                suggestions={positionAutocomplete.suggestions}
                loading={positionAutocomplete.loading}
                placeholder="Filter by position..."
                disabled={loading}
                showLogos={false}
                itemType="position"
                totalCount={positionAutocomplete.totalCount}
                hasMore={positionAutocomplete.hasMore}
                onLoadMore={positionAutocomplete.loadMore}
                currentPage={positionAutocomplete.currentPage}
                pageSize={10}
                onPageChange={positionAutocomplete.handlePageChange}
                showPagination={false} // Use infinite scroll instead
              />
            </div>

            <div className="filter-dropdown">
              <label htmlFor="industry-filter">Industry</label>
              <select
                id="industry-filter"
                value={selectedIndustry?.name || selectedIndustry?.id || 'All'}
                onChange={(e) => {
                  const industryValue = e.target.value;
                  if (industryValue === '' || industryValue === 'All') {
                    onIndustryChange(null);
                  } else {
                    const industry = industries.find(ind => ind.name === industryValue || ind.id === industryValue);
                    onIndustryChange(industry);
                  }
                }}
                className="filter-select"
                disabled={loading}
              >
                <option value="All">All</option>
                {industries.map(industry => (
                  <option key={industry.id || industry.name} value={industry.name || industry.id}>
                    {industryLabel(industry.name)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-dropdown">
              <label htmlFor="advisor-filter">Advisor</label>
              <select
                id="advisor-filter"
                value={selectedAdvisor?.name || '--All--'}
                onChange={(e) => {
                  const advisorName = e.target.value;
                  const advisor = advisorName === '--All--' ? null : advisors.find(adv => adv.name === advisorName);
                  onAdvisorChange(advisor);
                }}
                className="filter-select"
                disabled={loading}
              >
                <option value="--All--">--All--</option>
                {advisors.map(advisor => (
                  <option key={advisor.name} value={advisor.name}>
                    {advisor.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filters-dialog__footer">
            <button
              onClick={onApplyFilters}
              className={`apply-filter-button ${hasPendingFilterChanges ? 'has-changes' : ''}`}
              disabled={loading || !hasPendingFilterChanges}
              title={hasPendingFilterChanges ? 'Apply filter changes' : 'No filter changes to apply'}
            >
              {loading ? 'Applying...' : 'Apply Filters'}
              {hasPendingFilterChanges && <span className="changes-indicator" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default FiltersDialog;
